import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReCaptchaModule } from '@core/recaptcha/feature';
import { ReCaptchaInterceptor } from '@core/data-access';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyPortalFormModule } from '@portal/form/feature';
import { GenericApiModule } from '@core/generic-api/data-access';
import { PortalSharedIconModule } from '@portal/icon';
import { LayoutComponent } from '@portal/layout';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConfigModule } from '@core/app-config/feature';
import * as fromStore from '@portal/application/data-access/store';
import {
  AttachmentStore,
  AuthInterceptor,
  BoardResolutionFileStore,
  DataAccessStore,
  DataSetupApiService,
  MyInfoStore,
} from '@portal/application/data-access';
import { DEFAULT_CONFIG } from '@portal/data-access';
import { combineLatest, take } from 'rxjs';
import { environment } from '../environments/environment';
import { OverlayModule } from '@angular/cdk/overlay';
import { ActionReducer, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { PortalApplicationFormModule } from '@portal/application/form';
import { PDF_GENERATOR_CONFIG, PdfGeneratorConfig } from '@core/pdf-generator/data-access';
import { UserIdleModule } from '@core/user-idle';
import { MatDialogModule } from '@angular/material/dialog';

export const ngxMaskConfig: () => Partial<IConfig> = () => ({
  validation: false,
  thousandSeparator: ',',
  dropSpecialCharacters: false,
});

// console.log all actions
function debug(reducer: ActionReducer<any>): ActionReducer<any> {
  return function (state, action) {
    // console.group('@ngrx/store:', action.type);
    // console.log('state', state);
    // console.log('action', action);
    // console.groupEnd();
    return reducer(state, action);
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    MatDialogModule,
    BrowserModule,
    BrowserAnimationsModule,
    OverlayModule,
    HttpClientModule,
    AppRoutingModule,
    NgxMaskModule.forRoot(ngxMaskConfig),
    PortalSharedIconModule.forRoot(),
    FormlyModule.forRoot({ extras: { lazyRender: true, resetFieldOnHide: true } }),
    FormlyPortalFormModule,
    PortalApplicationFormModule,
    LayoutComponent,
    GenericApiModule.forRoot(),
    ReCaptchaModule.forRoot(),
    AppConfigModule.forRoot({
      configUrl: environment.apiConfig,
      initial: {
        apiHost: '',
        appId: 'dsme_portal',
        environment: environment.name,
        recaptchaSitekey: '',
        storageDrive: 's3',
        uploadFolder: 'cimb-sme-files-storage-local',
        urlPrefix: '/api/',
      },
    }),
    StoreModule.forRoot(
      {
        [fromStore.APPLICATION_FEATURE_KEY]: fromStore.applicationReducer,
        [fromStore.COUNTRY_FEATURE_KEY]: fromStore.countryReducer,
        [fromStore.DATA_SETUP_FEATURE_KEY]: fromStore.dataSetupReducer,
        [fromStore.NATIONALITY_FEATURE_KEY]: fromStore.nationalityReducer,
        [fromStore.PERSONNEL_FEATURE_KEY]: fromStore.personnelReducer,
      },
      {
        metaReducers: !environment.production ? [debug] : [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    EffectsModule.forRoot([
      fromStore.CountryEffects,
      fromStore.DataSetupEffects,
      fromStore.NationalityEffects,
      fromStore.PersonnelEffects,
    ]),
    !environment.production
      ? StoreDevtoolsModule.instrument({
          name: 'Portal',
          maxAge: 25, // Retains last 25 states
          logOnly: !environment.production, // Restrict extension to log-only mode
          autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        })
      : [],

    UserIdleModule.forRoot({ idle: DEFAULT_CONFIG.TIME_OUT_IN_SECONDS, timeout: 3, ping: 60 }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ReCaptchaInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (dataSetupApiService: DataSetupApiService) => {
        return () =>
          combineLatest([
            dataSetupApiService.getCountries().pipe(take(1)),
            dataSetupApiService.getNationalities().pipe(take(1)),
            dataSetupApiService.getDataSetup().pipe(take(1)),
          ]);
      },
      deps: [DataSetupApiService],
    },
    {
      provide: PDF_GENERATOR_CONFIG,
      useValue: <PdfGeneratorConfig>{
        headerPath: 'assets/core/templates/header.html',
        footerPath: 'assets/core/templates/footer.html',
      },
    },
    DataAccessStore,
    MyInfoStore,
    AttachmentStore,
    BoardResolutionFileStore,
    // fromStore.CountryFacade,
    // fromStore.DataSetupFacade,
    // fromStore.NationalityFacade,
    // fromStore.PersonnelFacade,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
